import React, { useCallback, useMemo, useEffect, useRef } from "react";
import { PropsWithChildren } from "react";
import * as Dialog from "@radix-ui/react-dialog";

import { type UniversalOverlayProps } from "./types";
import cx from "components/src/utils/cx";

import { Button } from "components/src/components/Button/Button";
import { Icon } from "components/src/components/Icon/Icon";
import { Heading, Body } from "components/src/components/Typography/Typography";
import debounce from "lodash-es/debounce";

import OverlayCloseIcon from "./components/OverlayCloseIcon";
import styles from "./scss/Overlay.module.scss";

export const Overlay = ({
  id,
  root,
  type = "modal",
  open = true,
  onOpenChange = () => {},
  showClose = true,
  stayOpen = false,
  size,
  content,
  children,
  headerDivider,
  ctaInline = true,
  ctaSticky = false,
  textAlign,
  zIndex,
  canInteractWithBackground = false,
  repositionBottomOnMobile = false,
  dataQa = "",
  onDismissHandler,
  onOpenHandler,
  ...rest
}: PropsWithChildren<UniversalOverlayProps>) => {
  const sheet = type.toLowerCase() === "sheet";
  const halfSheet = type.toLowerCase() === "half-sheet";
  const [left, center, right, justify] = [
    textAlign === "left",
    textAlign === "center",
    textAlign === "right",
    textAlign === "justify",
  ];

  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (
      window !== undefined &&
      repositionBottomOnMobile &&
      window?.innerWidth <= 768
    ) {
      const isAndroid = /Android/i.test(navigator.userAgent);
      const moveOverlay = debounce(() => {
        if (dialogRef.current && !isAndroid) {
          const viewportDifference =
            window?.innerHeight - (window?.visualViewport?.height ?? 0);

          dialogRef.current.style.paddingBottom = `calc(${viewportDifference}px + var(--spacing-lg))`;
        }
      }, 50);

      window?.visualViewport?.addEventListener("resize", moveOverlay);

      return () => {
        window?.visualViewport?.removeEventListener("resize", moveOverlay);
      };
    }
  }, [repositionBottomOnMobile, dialogRef]);

  const _root =
    root && document?.getElementById(root)
      ? document?.getElementById(root)
      : document?.body;
  if (open && !_root?.classList.contains(styles["cui__overlay"]))
    _root?.classList.add(styles["cui__overlay"]);
  const portalProps = root ? { container: _root } : {};

  const handleDismiss = useCallback((event) => {
    if (onDismissHandler) onDismissHandler(event);
  }, []);

  const handleOpen = useCallback((event) => {
    if (onOpenHandler) onOpenHandler(event);
  }, []);

  // If we configure it this way, Radix will continue to generate a random ID if we don't pass one in.
  let _id: { ["data-qa"]?: string, id?: string } = !!id ? { id } : {};
  dataQa ? _id = {..._id, ["data-qa"]: dataQa } : _id; 

  const { titleId, headerId, primaryCtaId, secondaryCtaId, closeCtaId, bodyId } = useMemo(()=>{
    const ids = content?.ids ? content?.ids : null;
    const titleId = ids && "title" in ids ? { id: ids.title?.id ?? "", ["data-qa"]: ids.title?.dataQa ?? "" } : {};
    const headerId = ids && "header" in ids ? { id: ids.header?.id ?? "", ["data-qa"]: ids.header?.dataQa ?? "" } : {};
    const closeCtaId = ids && "ctas" in ids && "close" in ids.ctas ? { id: ids.ctas?.close?.id ?? "", ["data-qa"]: ids.ctas?.close?.dataQa ?? "" } : {};
    const primaryCtaId = ids && "ctas" in ids && "primary" in ids.ctas ? { id: ids.ctas?.primary?.id ?? "", ["data-qa"]: ids.ctas?.primary?.dataQa ?? "" } : {};
    const secondaryCtaId = ids && "ctas" in ids && "secondary" in ids.ctas ? { id: ids.ctas?.secondary?.id ?? "", ["data-qa"]: ids.ctas?.secondary?.dataQa ?? "" } : {};
    const bodyId = ids && "body" in ids ? { id: ids.body?.id ?? "", ["data-qa"]: ids.body?.dataQa ?? "" } : {};
    return {
      titleId,
      headerId,
      primaryCtaId,
      secondaryCtaId,
      closeCtaId,
      bodyId
    }
  },[content?.ids]);

  return (
    <Dialog.Root
      modal={!canInteractWithBackground}
      open={open}
      onOpenChange={stayOpen ? () => {} : onOpenChange}
      {...rest}
    >
      <Dialog.Portal {...portalProps}>
        <Dialog.Overlay
          style={zIndex ? { zIndex } : {}}
          className={cx(
            {
              halfSheet,
            },
            `DialogOverlay`
          )}
        />
        <Dialog.Content
          {..._id}
          ref={dialogRef}
          style={zIndex ? { zIndex: zIndex + 1 } : {}}
          tabIndex={-1}
          aria-modal={true}
          onCloseAutoFocus={handleDismiss}
          onOpenAutoFocus={handleOpen}
          className={cx(
            {
              halfSheet,
              sheet,
              large: size === "lg",
              ctaSticky,
              hasHeader: !!content?.header,
              "cui-text-left": left,
              "cui-text-center": center,
              "cui-text-right": right,
              "cui-text-justify": justify,
            },
            "DialogContent"
          )}
        >
          {content?.header && (
            <div
              className="DialogHeader"
              {...headerId}
            >
              {content.header}
            </div>
          )}
          {showClose && !stayOpen && (
            <div className="DialogCloseIcon">
              <Button
                tabIndex={1}
                type="tertiary"
                aria-label="Close"
                onClick={() => {
                  onOpenChange(false);
                }}
                {...closeCtaId}
              >
                <OverlayCloseIcon />
              </Button>
            </div>
          )}
          {content?.subtitle && <div>{content.subtitle}</div>}
          {content?.icon && <Icon {...content.icon}></Icon>}
          {content?.title && (
            <Dialog.Title asChild={true}>
              <Heading
                size="xl-bold"
                {...titleId}
              >
                <>{content.title}</>
              </Heading>
            </Dialog.Title>
          )}

          {headerDivider && headerDivider !== "none" && (
            <div
              className={cx(
                {
                  small: headerDivider === "sm",
                  "cui-ml-auto": right,
                  "cui-mx-auto": center,
                  "cui-mr-auto": left,
                },
                "DialogContentDivider"
              )}
            />
          )}

          {content?.media && <div>{content.media}</div>}
          {content?.slot && <div>{content.slot}</div>}
          {(content?.body || children) && (
            <Dialog.Description asChild={true}>
              {children ? (
                children
              ) : content?.body ? (
                <Body
                  className={cx({ sheet }, "DialogBody")}
                  size="md"
                  {...bodyId}
                >
                  <>{content.body}</>
                </Body>
              ) : null}
            </Dialog.Description>
          )}
          {content?.list && content.list.length > 0 && (
            <ol className="DialogList">
              {content.list.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ol>
          )}
          {content?.disclaimer && <div>{content.disclaimer}</div>}

          {(content?.primaryCta || content?.secondaryCta) && (
            <div
              className={cx(
                {
                  ctaSticky,
                  ctaInline,
                  sheet,
                },
                "DialogButtonContainer"
              )}
            >
              {content?.primaryCta && (
                <Button
                  tabIndex={1}
                  {...content.primaryButton}
                  {...primaryCtaId}
                >
                  {content.primaryCta}
                </Button>
              )}
              {content?.secondaryCta && (
                <Button
                  tabIndex={1}
                  {...{ type: "minimal", ...content.secondaryButton }}
                  {...secondaryCtaId}
                >
                  {content.secondaryCta}
                </Button>
              )}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
